import { useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { useMapStore } from "stores/map";

import { useUserEvents } from "hooks/useUserEvents";

import { FilterAbortHolder } from "api/filter";

import { EntityKind } from "models/entityKind";

import { useDashboardContext } from "components/dashboard/hooks";
import { WellCardViewSource } from "components/well-card-panel/constants";

import { useMapContextSafe } from "../hooks/useMapContext";
import {
  abortPendingJump,
  createJumpAbortHolder,
  jumpToWells
} from "../utils/jumpToWells";
import {
  SINGLE_WELL_MAX_ZOOM,
  claimWellOpen,
  isLatestWellOpen,
  isMapOnScreen,
  selectAndOpenWell,
  wellMidpoint
} from "../utils/openSingleWell";

export const UNABLE_TO_FIND = "Unable to find wells";
export const UNABLE_TO_LOOK_UP = "Unable to look up wells. Please try again.";

/**
 * Room around the fitted box.
 *
 * Wider than the default because the extent is built from bottom-hole
 * coordinates — a well's drawn geometry reaches past the box being fitted, so
 * fitting it exactly slices the outermost wells.
 *
 * Left is wider still because the map toolbar is pinned over the map's
 * top-left corner (Map.tsx renders it `absolute pin-topleft`), so the left edge
 * of the viewport is not the left edge of the usable map. Uniform padding
 * cannot express that, which is why wells kept landing under the toolbar and
 * reading as clipped on that side specifically.
 *
 * Deliberately NOT a lower zoom cap. fitBounds takes
 * `Math.min(fittedZoom, maxZoom)`, so the cap only binds when the box is small
 * enough that the fit would go closer — and in that case the box already sits
 * well inside the viewport and cannot clip. Lowering the cap would only pull
 * every tight-cluster jump further out for no gain.
 */
const JUMP_FIT_PADDING = { top: 60, bottom: 60, right: 60, left: 140 };

export type JumpStatus = "idle" | "busy" | "done";

/** "3 wells found for 1 entry" — wells-for-entries, never a match ratio: the
 *  two numbers count different things. See submitJump's return. */
export function formatMatchedCount({ matched, requested }: MatchedCount): string {
  const wells = matched === 1 ? "well" : "wells";
  const entries = requested === 1 ? "entry" : "entries";
  return `${matched} ${wells} found for ${requested} ${entries}`;
}

export interface MatchedCount {
  readonly matched: number;
  readonly requested: number;
}

/**
 * The shared jump behaviour behind BOTH entry points — the Jump to Location
 * "Well" tab and the Ctrl+A quick-entry modal — which is what stops the two
 * from drifting. Owns no text state: a textarea and a single-line input commit
 * their value differently.
 */
export function useWellJump(source: WellCardViewSource) {
  const [status, setStatus] = useState<JumpStatus>("idle");
  const [error, setError] = useState<Nullable<string>>(null);
  const [matched, setMatched] = useState<Maybe<MatchedCount>>(undefined);

  const setFlyToExtent = useMapStore((s) => s.setFlyToExtent);
  const setJumpHighlightWells = useMapStore((s) => s.setJumpHighlightWells);
  const mapbox = useMapContextSafe()?.mapbox;
  const dispatch = useDispatch();
  const { isPopoutDashboard } = useDashboardContext();
  const { sendWellCardViewedEvent } = useUserEvents();

  // Only the id that was current when a submit STARTED and is still current
  // when it finishes may write to the map — otherwise a slower earlier
  // response lands last and flies the map somewhere stale.
  const requestIdRef = useRef(0);
  // This instance's OWN single-flight slot — see createJumpAbortHolder. Lazily
  // initialised: useRef(createJumpAbortHolder()) builds one per render.
  const abortHolderRef = useRef<Nullable<FilterAbortHolder>>(null);
  if (!abortHolderRef.current) {
    abortHolderRef.current = createJumpAbortHolder();
  }

  function resetFeedback() {
    setError(null);
    setMatched(undefined);
  }

  /**
   * Invalidates any in-flight submit WITHOUT touching the highlight already on
   * the map: dismissing a surface is not a request to undo an earlier jump, but
   * a response still in flight must not fly the camera afterwards.
   */
  function cancelPending() {
    resetFeedback();
    setStatus("idle");
    requestIdRef.current++;
    // Bumping the request id only discards the RESPONSE; aborting stops the POST
    // too, so a dismissed jump leaves no throwaway filter behind.
    abortPendingJump(abortHolderRef.current);
  }

  /** Drops the durable highlight and invalidates any in-flight submit. */
  function clearJump() {
    cancelPending();
    claimWellOpen();
    setJumpHighlightWells([]);
  }

  function openWell(uwi: string) {
    selectAndOpenWell(uwi, dispatch);
    // A popped-out dashboard mounts no well card, so nothing was viewed.
    if (isPopoutDashboard) return;
    sendWellCardViewedEvent({ uwi, entityKind: EntityKind.Well, source });
  }

  /**
   * Resolves `text` and moves the camera. Returns whether the two counts line
   * up — NOT a completeness claim; see the return statement.
   */
  async function submitJump(text: string): Promise<boolean> {
    setStatus("busy");
    setError(null);
    const requestId = ++requestIdRef.current;
    const jumpToken = claimWellOpen();
    // Superseded within this surface, or by a newer jump from any other
    // surface or Escape — either way this result must not touch the map. Only
    // a newer submit here takes the status over, so any other claim resets it.
    const isSuperseded = () => {
      if (requestId !== requestIdRef.current) return true;
      if (isLatestWellOpen(jumpToken)) return false;
      setStatus("idle");
      return true;
    };

    const result = await jumpToWells(text, abortHolderRef.current);

    if (isSuperseded()) return false;

    // `Result.Ok.ok` is typed `boolean`, not `true`, so `!result.ok` alone does
    // not narrow the union.
    if (!result.ok && "error" in result) {
      setStatus("idle");
      if (result.error === "no-match") {
        setError(UNABLE_TO_FIND);
      } else if (result.error === "failed") {
        setError(UNABLE_TO_LOOK_UP);
      }
      // "cancelled" (superseded) and "empty" (submit was already disabled) show
      // nothing.
      return false;
    }

    const success = result.value;
    // Ctrl+A reaches a map out of sight too. There a one-well jump opens the
    // card and leaves the map alone: no camera move or highlight.
    if (success.uwis.length === 1 && mapbox && !isMapOnScreen(mapbox)) {
      openWell(success.uwis[0]);
      setStatus("done");
      setMatched({ matched: success.matchedCount, requested: success.requestedCount });
      return success.matchedCount === success.requestedCount;
    }
    let extent = success.extent;
    if (success.uwis.length === 1) {
      // This jump's own controller, so a cancel stops this request too.
      const signal = abortHolderRef.current.current?.signal;
      const midpoint = await wellMidpoint(success.uwis[0], signal);
      if (isSuperseded()) return false;
      if (midpoint) {
        const [x, y] = midpoint;
        extent = { minx: x, miny: y, maxx: x, maxy: y };
      }
    }
    if (success.uwis.length === 1) {
      openWell(success.uwis[0]);
    }
    // A jump frames itself, scoped to this command so the other fly-to
    // producer keeps the framing it has always had. See JUMP_FIT_PADDING.
    setFlyToExtent(
      extent,
      success.uwis.length === 1
        ? {
            padding: JUMP_FIT_PADDING,
            maxZoom: SINGLE_WELL_MAX_ZOOM,
            clearWellCard: true
          }
        : { padding: JUMP_FIT_PADDING }
    );
    setJumpHighlightWells(success.uwis);
    setStatus("done");
    setMatched({
      matched: success.matchedCount,
      requested: success.requestedCount
    });

    // NOT a completeness claim: matchedCount counts WELLS, requestedCount counts
    // ENTRIES, and one entry can resolve to many wells, so no comparison of the
    // two answers "did every entry resolve". The service drops unmatched entries
    // without reporting which, so entry-level attribution is not available here
    // at all. This answers only "do the two numbers line up" — i.e. is there a
    // count worth showing.
    return success.matchedCount === success.requestedCount;
  }

  return {
    status,
    error,
    matched,
    submitJump,
    clearJump,
    cancelPending,
    resetFeedback
  };
}
